import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

const Nav = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 248, 245, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  /* No border-bottom — tonal separation via background colour only (design system rule) */
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Brand = styled(Link)`
  font-family: 'Epilogue', sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #a0412d;
  text-decoration: none;

  &:hover {
    color: #721f0f;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ $active }) => $active ? '#a0412d' : '#665d58'};
  text-decoration: none;
  transition: color 0.2s ease;
  padding-bottom: 2px;
  border-bottom: ${({ $active }) => $active ? '2px solid #a0412d' : '2px solid transparent'};

  &:hover {
    color: #006b63;
    border-bottom-color: #006b63;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #38312d;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    background-color: currentColor;
    border-radius: 2px;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  &[aria-expanded="true"] span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }
  &[aria-expanded="true"] span:nth-child(2) {
    opacity: 0;
  }
  &[aria-expanded="true"] span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }
`;

const MobileMenu = styled.div`
  display: none;
  flex-direction: column;
  padding: 0 2rem 1.5rem;
  gap: 0.25rem;

  @media (max-width: 768px) {
    display: ${({ $open }) => $open ? 'flex' : 'none'};
  }
`;

const MobileLink = styled(Link)`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ $active }) => $active ? '#a0412d' : '#38312d'};
  padding: 0.6rem 0;
  text-decoration: none;
  /* No border — use spacing only (design system rule: no 1px separator lines) */

  &:hover {
    color: #006b63;
  }
`;

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'About', to: '/about' },
  { label: 'Skills', to: '/skills' },
  { label: 'In the Media', to: '/in-the-media' },
  { label: 'Vital Statistics', to: '/vital-statistics' },
  { label: 'Contact', to: '/contact' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <Nav>
      <NavInner>
        <Brand to="/">Sari Angus</Brand>
        <NavLinks>
          {navItems.map(({ label, to }) => (
            <NavLink key={to} to={to} $active={pathname === to}>
              {label}
            </NavLink>
          ))}
        </NavLinks>
        <HamburgerButton
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          onClick={() => setMenuOpen(o => !o)}
        >
          <span />
          <span />
          <span />
        </HamburgerButton>
      </NavInner>
      <MobileMenu id="mobile-nav" $open={menuOpen}>
        {navItems.map(({ label, to }) => (
          <MobileLink
            key={to}
            to={to}
            $active={pathname === to}
            onClick={() => setMenuOpen(false)}
          >
            {label}
          </MobileLink>
        ))}
      </MobileMenu>
    </Nav>
  );
};

export default Header;
